// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMModel } from '@form/model/lm-model';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMFormContentBase } from './lm-form.model';
import { lmMatrixRowFormatter } from '@list/formatters/lm-matrixrow-formatter';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMModelChangedEvent } from './model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LMFormContext, LMFormMatrixService, LMFormPagingService } from './services';
import { LMFormTransactionService } from './services/lm-form-transaction.service';
import { AppConstants } from '@utils/app.constants';
import {
  lmBooleanSerializer,
  lmDateTimeSerializer,
  lmPercentSerializer,
} from '@list/editors';
import { lmLookupClickFunction } from '@list/editors/lm-lookup-click.function';
import {
  LMActionService,
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService,
  LMUrlUtilService
} from '@services/index';

import * as urltemplate from 'url-template';
import { LMGlobalUtilService } from '@app/utils';
import { SohoDataGridComponent } from 'ids-enterprise-ng';
import {LMInvalidFieldEvent} from "@app/components";
import { LMListDatagridWrapperComponent } from '@components/list/lm-list-datagrid-wrapper.component';

/**
 * Created by dboehm on 11/22/2017.
 */
@Component({
  selector: 'lm-form-content-matrix',
  standalone: false,
  templateUrl: 'lm-form-content-matrix.component.html',
  styleUrls: [ 'lm-form-content-matrix.component.scss' ],
  providers: [ LMFormMatrixService, LMFormatterService ]
})
export class LMFormContentMatrixComponent  implements AfterViewInit, OnInit, OnDestroy, LMFormContentBase {
  @Input() formUrlInfo: LMFormUrlInfo
  @Input() formSpec: LMFormSpec;
  @Input() formOptions: LMFormOptions = null;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() formState: LMFormState;
  @Input() placeHolder: ViewContainerRef;
  @Input() matrixFormSpec: LMBusMatrixFormSpec;
  @Input() matrixView: LMDataView;
  @Input() columnList: Array<LMDataView>;

  @Output() dataViewLoaded: EventEmitter<LMDataView> = new EventEmitter<LMDataView>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() contentModified = new EventEmitter<any>();
  @Output() matrixActionEvent = new EventEmitter<LMMatrixActionEvent>();

  @ViewChild('DatagridWrapper') datagridWrapper: LMListDatagridWrapperComponent;

  get sohoDataGridComponent(): SohoDataGridComponent {
    return this.datagridWrapper?.sohoDataGridComponent;
  }

  @ViewChild('matrixDialog', { read: ViewContainerRef, static: true }) matrixDialog: ViewContainerRef;

  public contextForm: LMContextForm;
  public frozenColumnWidth = 300;
  public showRowFilter = false;
  public showDateRangeFilter = false;

  public get datagridId(): string {
    if (!this.matrixFormSpec) {
      return null;
    }
    return this.matrixFormSpec.name + '_datagrid';
  }

  public dateModel = { date: '' };
  public sohoDatePickerOptions: SohoDatePickerOptions;
  public uiLoadingIndicatorTimeout = 0;

  private subscriptionManager: Subject<void> = new Subject<void>();
  private showMoreButtonText: string;
  private showLessButtonText: string;
  private showDetailFields = false;

  constructor(
    private ngZone: NgZone,
    private lmActionService: LMActionService,
    protected lmBusyIndicatorService: LMBusyIndicatorService, // todo why is this protected?
    private lmConditionUtilService: LMConditionUtilService,
    private lmContextService: LMContextService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFormMatrixService: LMFormMatrixService,
    private lmFormTransactionService: LMFormTransactionService,
    private lmFormPagingService: LMFormPagingService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmFormatterService: LMFormatterService,
  ) {
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit(): void {
    this.contextForm = this.matrixFormSpec.contextForm;

    if (this.formState.contextFormExpanded === undefined) {
      this.formState.contextFormExpanded = true;
    }

    this.showMoreButtonText = Soho.Locale.translate('ShowMore');
    this.showLessButtonText = Soho.Locale.translate('ShowLess');

    if (this.matrixFormSpec.dateFilterFieldId) {
      this.showDateRangeFilter = true;

      this.sohoDatePickerOptions = {
        placeholder: 'MM/yyyy',
        showMonthYearPicker: true,
        hideDays: true,
        dateFormat: 'MM/yyyy'
      };
    }

    // TODO: Not yet implemented
    // if (this.matrixFormSpec.rowSearchField) {
    //   this.showRowFilter = true;
    // }
  }

  get contextFormExpanderButtonText(): string {
    return this.formState.contextFormExpanded ? this.showLessButtonText : this.showMoreButtonText;
  }

  ngAfterViewInit() {
    if (this.contextForm) {
      // initialize collapsible context form.
      $('.expandable-area').expandablearea()
        .on('expand', () => this.formState.contextFormExpanded = true)
        .on('collapse', () => this.formState.contextFormExpanded = false);
    }

    this.initialize();
    this.addListeners();
  }

  ngOnDestroy() {
    this.subscriptionManager.next();
    this.subscriptionManager.complete();
  }

  public isModified(): boolean {
    return this.getDirtyCells().length > 0;
  }

  public runMatrixToggleDetail(showDetail: boolean) {
    this.showDetailFields = showDetail;
    this.sohoDataGridComponent.data = this.buildMatrixData(this.matrixView.rowSet);
  }

  public runRefresh(): void {
    // do nothing because the model may not be updated yet.
    // listening to the model.dataUpdated event to refresh the data
  }

  public runSaveAction(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.markCellDataViewsChanged();
      const formContext: LMFormContext = {
        formUrlInfo: this.formUrlInfo,
        formSpec: this.formSpec,
        formModel: this.model,
        placeholder: this.placeHolder
      };
      const formActions = this.formSpec.busClassSpec.actions;
      const actionIndexes = this.matrixView.actionIndexes;
      const actionSpec = this.lmActionService.getDefaultUpdateAction(formActions, actionIndexes, true, true);

      this.lmFormTransactionService.runSaveAction(formContext, actionSpec, this.matrixView).subscribe(
        transactionState => {
          if (transactionState.isError) {
            this.lmErrorUtilService.handleError(
              transactionState.response.message,
              transactionState.response.details,
              transactionState.response.status,
              transactionState.response.message);
          } else if (transactionState.isCanceled) {
            // TODO DPB
          } else {
            const result: LMActionResult = transactionState.getTransactionResult();

            if (result) {
              this.handleTransactionStatus(result);
              if (transactionState.isSuccess()) {
                // this.model.setData(result.dataView, { source: { type: 'transaction' } }); // need to test this, want to make sure that the context form is updated
                const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ActionCompletedSuccessfully'), [ result.actionSpec.label ]);
                const message = result.message || Soho.Locale.translate('ActionSuccessful');
                this.lmGlobalUtilService.displayToastMessage(title, message, AppConstants.TRA_ACTION_COMPLETED);

                if (this.sohoDataGridComponent) {
                  this.sohoDataGridComponent.clearDirty();
                }
              }
            }
          }

          this.lmBusyIndicatorService.hideBusyIndicator();
          observer.next(transactionState.isSuccess());
          observer.complete();
        });
    });
  }

  private markCellDataViewsChanged() {
    if (this.sohoDataGridComponent) {
      // const dirtyCells: Array<any> = this.sohoDataGridComponent.dirtyCells();
      // DPB hack until soho update to return dirty cells
      const hackedSohoDataGrid: any = this.sohoDataGridComponent;
      const dirtyArray: Array<any> = hackedSohoDataGrid.datagrid.dirtyArray;
      const rowData = this.matrixView.rowSet.data;
      for (let i = 0, l = rowData.length; i < l; i++) {
        const rowDataView = rowData[i];
        const dirtyRow = dirtyArray[i];
        if (dirtyRow !== undefined) {
          for (let j = 0, jl = dirtyRow.length; j < jl; j++) {
            const dirtyColumn = dirtyRow[j];
            if (dirtyColumn !== undefined && dirtyColumn.isDirty) {
              const cellDataView = rowDataView.data[j - 1];
              if (cellDataView) {
                cellDataView.matrixCellChanged = true;
              }
            }
          }
        }
      }
    }
  }

  private handleTransactionStatus(result: LMActionResult) {
    if (result.status === AppConstants.VIEW_EXCEPTION) {
      this.lmErrorUtilService.handleError(result.message, result.details, result.status);
    } else if (result.status !== AppConstants.STATUS_COMPLETED && result.status !== AppConstants.CONFIRMATION_REQUIRED) {
      // If the status is confirmation required, it may be due to the user cancelling out of runtime confirmation.
      this.lmErrorUtilService.handleError(result.message, result.details, result.status, result.message);
    }
  }
  saveComponentState(): void {
  }

  // EVENT HANDLING
  onDataViewLoaded(dataView: LMDataView) {
    this.dataViewLoaded.emit(dataView);
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onFieldEvent(event: LMFieldEvent) {
    // context form events are handled by the form.component
    this.fieldEvent.emit(event);
  }

  onCellChange(event: SohoDataGridCellChangeEvent) {
    if (!event) {
      return;
    }

    const dirtyRows = this.sohoDataGridComponent.dirtyRows();
    this.sohoDataGridComponent.clearRowError(event.row);

    if (dirtyRows) {
      const dirtyRow = dirtyRows.find((row) => event.row === row.id - 1);
      if (dirtyRow) {
        this.sohoDataGridComponent.rowStatus(event.row, 'in-progress', Soho.Locale.translate('RowDataChanged'));
      }
    }

    this.onContentModified(event);
  }

  onContextMenu(event: SohoDataGridRowClicked): void {
  }

  onRowActivated(event: SohoDataGridRowActivated) {
  }

  onRowDeactivated(event: SohoDataGridRowActivated) {
  }

  onDateRangeFilter() {
    // If date range has no value reset data to initial
    if (this.dateModel.date.length === 0) {

      const urlCompiledTemplate = urltemplate.parse(this.formUrlInfo.initialDataUrl);
      const initialDataUrl = urlCompiledTemplate.expand({ resourceId: this.matrixView.resourceId, actionName: this.formUrlInfo.action });

      this.lmFormPagingService
        .loadRecord(initialDataUrl)
        .subscribe((r) => {
          this.update(r);
        },
        error => {
          this.handleError(error);
        });
    } else {
      this.filter(this.dateModel.date);
    }
  }

  public onPrevious() {
    const date = this.dateModel.date;
    const month = date.split('/')[0];
    const year = date.split('/')[1];
    const previousYear = new Date(year).getFullYear();
    const months = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 ];
    const newMonth = months[Number(month) - 1];
    const newDate = `${newMonth === 0 ? 12 : newMonth}/${newMonth === 0 ? previousYear : year}`;
    this.filter(newDate);

    this.dateModel.date = newDate;
  }

  public onNext() {
    const date = this.dateModel.date;
    const month = date.split('/')[0];
    const year = date.split('/')[1];
    const nextYear = new Date(year).getFullYear() + 2;
    const months = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13 ];
    const newMonth = months[Number(month)];
    const newDate = `${newMonth === 13 ? '01' : newMonth}/${newMonth === 13 ? nextYear : year}`;
    this.filter(newDate);

    this.dateModel.date = newDate;
  }

  private filter(date: string) {
    const url = this.lmUrlUtilService.getMatrixDateFilterUrl(this.formUrlInfo, this.matrixView.resourceId, date, this.matrixFormSpec.dateFilterFieldId);
    this.lmFormPagingService
      .loadRecord(url)
      .subscribe((r) => {
        this.update(r);
      },
      error => {
        this.handleError(error);
      });
  }

  /** Used to Get checkbox in header
   * @param event
   */
  onDataGridClicked(event: MouseEvent) {
    if (event.target === undefined) {
      return;
    }

    const target = event.target as HTMLElement;

    if ((target.nodeName && target.nodeName !== 'LABEL') || !target.classList.contains('lm-checkbox-label')) {
      return;
    }

    const displayFieldIndex = Number(target.dataset.displayfieldindex);
    const displayField = this.matrixFormSpec.columns[displayFieldIndex];
    const dataView = this.matrixView.columnList[target.dataset.columnindex];
    const element = target.closest('DIV').querySelectorAll('input')[0];

    // Wait for datagrid to update checkbox to ensure 'element.checked' is in the right state
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.runColumnCheckControlAction(displayField, dataView, element.checked, element);
      });
    });
  }

  onRowClicked(event: SohoDataGridRowClicked) {
    if (event && event.originalEvent) {
      const eventTarget = event.originalEvent.target as HTMLElement;

      // Safety check
      if (event.item === undefined || event.item.data === undefined || eventTarget.dataset === undefined) {
        return;
      }

      const column = event.cell;
      const rowDataView: LMDataView = event.item;
      const cellDataView: LMDataView = rowDataView.data[column];
      const displayField: LMListDisplayField = eventTarget.dataset.navigationParams ? JSON.parse(eventTarget.dataset.navigationParams) : null;

      if (eventTarget.nodeName === 'A') {
        this.lmFormMatrixService.runMatrixLinkNavigation(this.formUrlInfo, this.formSpec, cellDataView, displayField);
      }

      if (eventTarget.nodeName === 'LABEL' && displayField.type === AppConstants.CHECKCONTROL) {
        const element = eventTarget.closest('DIV').querySelectorAll('input')[0];
        this.toggleCheckBox(element);
        this.runCellCheckControlAction(displayField, cellDataView, element.checked, element);
      }
    }
  }

  onRunMatrixActionEvent(event): Observable<any> {
    this.matrixActionEvent.emit(event);
    return this.matrixActionEvent;
  }

  onRowDoubleClicked(event: SohoDataGridRowClicked): void {
  }

  onRowSelected(event: SohoDataGridSelectedEvent) {
  }

  handleBlockingErrorField(event: LMInvalidFieldEvent) {
    event.alert.scrollIntoView(false);
  }

  private isChecked(displayField, cellDataView): boolean {
    if (displayField.hasCheckedState) {
      return cellDataView.fields[displayField.bindId].value as boolean;
    }

    return false;
  }

  private runColumnCheckControlAction(
    displayField: LMListDisplayField,
    dataView: LMDataView,
    isChecked?: boolean,
    element?: HTMLInputElement
  ) {
    const validActions = this.lmFormMatrixService.getValidCheckControlActions(displayField, dataView, isChecked);

    this.onRunMatrixActionEvent(<LMMatrixActionEvent>{ actionTarget: 'column', actionSpec: validActions[0], dataView, callBack: {
      next: (complete) => {
        if (element && !complete) {
          this.toggleCheckBox(element);
        }
      },
      error: (err) => {
        // console.log('error')
      },
      complete: (c) => {
        // console.log('done')
      }
    } });
  }

  private runCellCheckControlAction(
    displayField: LMListDisplayField,
    dataView: LMDataView,
    isChecked?: boolean,
    element?: HTMLInputElement
  ) {
    const validActions = this.lmFormMatrixService.getValidCheckControlActions(displayField, dataView, isChecked);

    this.onRunMatrixActionEvent(<LMMatrixActionEvent>{ actionTarget: 'cell', actionSpec: validActions[0], dataView, callBack: {
      next: (complete) => {
        if (element && !complete) {
          this.toggleCheckBox(element);
        }
      },
      error: (err) => {
        // console.log('error')
      },
      complete: (c) => {
        // console.log('done')
      }
    } });
  }

  private toggleCheckBox(checkboxElement: HTMLInputElement) {
    checkboxElement.checked = !checkboxElement.checked ;
  }

  private initialize(): void {
    this.matrixView = this.model.getData();
    this.sohoDataGridComponent.gridOptions = Object.assign({}, this.buildDataGridOptions());

    if (this.matrixView.rowSet) {
      this.sohoDataGridComponent.data = this.buildMatrixData(this.matrixView.rowSet);
    } else {
      this.sohoDataGridComponent.data = [];
    }
  }

  private update(matrixView: LMDataView) {
    this.matrixView = Object.assign({}, matrixView);
    this.sohoDataGridComponent.gridOptions = Object.assign({}, this.buildDataGridOptions());

    if (this.matrixView.rowSet) {
      this.sohoDataGridComponent.data = this.buildMatrixData(this.matrixView.rowSet);
    } else {
      this.sohoDataGridComponent.data = [];
    }
  }

  private addListeners() {
    this.model.modelChanged.pipe(takeUntil(this.subscriptionManager)).subscribe((modelChangedEvent: LMModelChangedEvent) => {
      this.matrixView = this.model.getData();
      this.sohoDataGridComponent.columns = this.buildGridColumnHeaders();

      if (this.matrixView.rowSet) {
        this.sohoDataGridComponent.data = this.buildMatrixData(this.matrixView.rowSet);
      } else {
        this.sohoDataGridComponent.data = [];
      }
    });
  }

  private dataGridSource = (request: SohoDataGridSourceRequest, response: SohoDataGridResponseFunction) => {
    // TODO DPB implement with paging and filtering.
    switch (request.type) {
      // case 'initial':
      //   this.initialPage(request, response);
      //   break;
      //
      // case 'first':
      //   this.firstPage(request, response);
      //   break;
      //
      // case 'last':
      //   this.lastPage(request, response);
      //   break;
      //
      // case 'next':
      //   this.nextPage(request, response);
      //   break;
      //
      // case 'prev':
      //   this.previousPage(request, response);
      //   break;
      //
      // case 'sorted':
      //   this.sortPage(request, response);
      //   break;
      //
      // case 'filtered':
      //   this.filterPage(request, response);
      //   break;
      //
      // case 'refresh':
      //   this.reloadPage(request, response);
      //   break;
    }
  };

  public buildDataGridOptions(): SohoDataGridOptions {
    const gridOptions: SohoDataGridOptions = {
      selectable: 'single',
      cellNavigation: true,
      sizeColumnsEqually: true,
      spacerColumn: true,
      columns: this.buildGridColumnHeaders(),
      disableClientSort: true,
      disableClientFilter: true,
      indeterminate: true,
      menuId: this.matrixFormSpec.name + '_ContextMenu',
      editable: !this.matrixFormSpec.matrixFormNotEditable,
      paging: false,
      pagesize: 10,
      pagesizes: [ 5, 10, 20, 50, 100 ],
      showPageSizeSelector: false,
      filterable: this.matrixFormSpec.rowSearchField !== undefined, // DPB this is not needed if the affordance is not in the filterbar
      rowHeight: 'short', // (short, medium or normal)
      saveUserSettings: {
        columns: false,
        rowHeight: false,
        sortOrder: false,
        pagesize: false,
        activePage: false,
        filter: false
      },
      toolbar: null,
      actionableMode: true,
      showDirty: true, // works in conjunction with rowStatus column:
    };

    // Now using 'light datagrid header color
    gridOptions['headerBackgroundColor'] = 'light';

    // Set frozen columns
    gridOptions.frozenColumns = this.getFrozenColumns();

    return gridOptions;
  }

  private buildGridColumnHeaders(): SohoDataGridColumn[] {
    const columnHeaderList: SohoDataGridColumn[] = [];

    // MATRIX ROW SET HEADER
    columnHeaderList.push(this.buildRowColumnHeader());

    // MATRIX COLUMN SET HEADER
    if (this.matrixView.columnList) {
      this.matrixView.columnList.forEach((h, index) => columnHeaderList.push(this.buildMatrixColumnHeader(index, h)));
    }

    return columnHeaderList;
  }

  private getFrozenColumns() {
    return { left: [ 'ROW_HEADER' ] };
  }

  private buildRowColumnHeader(): ListGridColumn {
    const listGridColumn: ListGridColumn = {};
    listGridColumn.id = 'ROW_HEADER';
    listGridColumn.matrixIndex = 0;
    listGridColumn.formatter = this.LMMatrixRowFormatter;
    listGridColumn.sortable = false;
    listGridColumn.name = '';
    listGridColumn.width = `${this.frozenColumnWidth}px`;

    this.matrixFormSpec.columns.forEach((displayField: LMListDisplayField) => {
      if (displayField.condition) {
        if (this.lmConditionUtilService.isValid([ displayField.condition ], this.matrixView)) {
          return;
        }
      }

      const title = displayField.label
        ? this.lmFormatUtilService.formatMessageRule(displayField.label, this.matrixView)
        : '';

      listGridColumn.name = `${listGridColumn.name}<div class="lm-matrix-element">${title}</div>`;
    });

    return listGridColumn;
  }

  private buildMatrixColumnHeader(id: number, columnDataView: LMDataView): ListGridColumn {
    // TODO are button linke (displayFIeld.navigation and field links (displayField.linkNavigation working in the header
    // Also why are we not checking for displayFIeldVisibiliyt
    let isColumnActionable = false;
    const uniqueIdentifier = `${id + 1}`;
    const listGridColumn: ListGridColumn = {};
    listGridColumn.id = uniqueIdentifier;
    listGridColumn.matrixIndex = id;
    listGridColumn.sortable = false;
    listGridColumn.formatter = this.LMMatrixRowFormatter;
    listGridColumn.name = '';

    // iterate the column fields
    this.matrixFormSpec.columns.forEach((listDisplayField: LMListDisplayField, index: number) => {
      if (listDisplayField.condition) {
        if (this.lmConditionUtilService.isValid([ listDisplayField.condition ], columnDataView)) {
          return;
        }
      }
      const colTitle = listDisplayField.label
        ? this.lmFormatUtilService.formatMessageRule(listDisplayField.label, columnDataView)
        : '';

      const fieldId: string = listDisplayField.fieldId;
      const columnField: LMField = columnDataView.fields[fieldId];
      const columnValue = columnField != null ? this.lmFormatUtilService.toDisplayValue(columnField.value, listDisplayField.type) : colTitle;
      let columnFieldHtml = '';

      if (AppConstants.CHECKCONTROL === listDisplayField.type) {
        const checkControlId = `${AppConstants.CHECKCONTROL}${index}'_col'${id}`;
        columnFieldHtml = `
          <div class="lm-matrix-element field" style="margin:0;">
            <input id="${checkControlId}" type="checkbox" class="checkbox" name="${checkControlId}" ${ this.isChecked(listDisplayField, columnDataView) ? 'checked' : ''} />
            <label
              for="${checkControlId}"
              class="lm-checkbox-label checkbox-label"
              data-columnIndex="${id}"
              data-displayFieldIndex="${index}"
            ></label>
          </div>
        `;
      } else {
        const classes = String(columnValue).length > 250 ? 'lm-ellipsis lm-matrix-element' : 'lm-matrix-element';
        columnFieldHtml = `<div class="${classes}" title="${columnValue}">${columnValue}</div>`;
      }

      listGridColumn.name = `${listGridColumn.name}${columnFieldHtml}`;
      listGridColumn.width = (columnValue < 250) ? `${columnValue.toString().length * 10}px` : '250px';

      if (!isColumnActionable) {
        isColumnActionable = listDisplayField.linkNavigation || listDisplayField.type === AppConstants.CHECKCONTROL ? true : false;
      }
    });

    listGridColumn.isActionable = isColumnActionable;
    if (!this.matrixFormSpec.matrixFormNotEditable && !this.lmContextService.drillAroundWindow()) {
      // Matrix only supports editing the first column field!
      this.setColumnEditor(listGridColumn, id);
    }
    return listGridColumn;
  }

  private setColumnEditor(column: ListGridColumn, columnIndex: number): void {
    const cellDisplayField: LMListDisplayField = this.matrixFormSpec.cells[0];
    const cellFieldSpec = this.matrixFormSpec.fields[cellDisplayField.fieldId];

    column.displayField = cellDisplayField;
    column.cellFieldSpec = cellFieldSpec;
    column.field = 'data.' + columnIndex + '.fields.' + cellDisplayField.fieldId + '.value';
    // column.editability = landmark.listutil.IsMatrixCellEditable;
    const type = cellDisplayField.type.toUpperCase();
    if (cellDisplayField.type !== AppConstants.CHECKCONTROL) {
      // column.isEditable = this.LMIsEditable;
    }

    if (cellDisplayField.isStaticStateField) {
      column.editor = Soho.Editors.Dropdown;
      column.options = this.lmFormatUtilService.fieldStatesToOptionsArray(cellDisplayField.states, false);
    } else if (cellDisplayField.isUserDefinedStateField) {
      column.editor = Soho.Editors.Dropdown;
      column.editorOptions = {
        editable: false,
        // source:   this.LMStateFieldSource
      };
    } else {
      switch (type) {
        case AppConstants.NUMERIC:
        case AppConstants.BCD:
        case AppConstants.PERCENT:
          column.editor = Soho.Editors.Input;
          const percentAdjustment: number = (type === AppConstants.PERCENT) ? 2 : 0;
          const allowDecimal: boolean = (cellFieldSpec.decSize !== undefined && cellFieldSpec.decSize - percentAdjustment > 0);
          const decimalLimit: number = (allowDecimal) ? cellFieldSpec.decSize - percentAdjustment : 0;
          const integerLimit: number = (allowDecimal) ? cellFieldSpec.size - decimalLimit : cellFieldSpec.size;
          column.maskOptions = {
            process: 'number',
            patternOptions: {
              allowDecimal,
              allowNegative: !cellDisplayField.positiveOnly,
              integerLimit,
              decimalLimit,
              allowThousandsSeparator: false,
              symbols: {
                decimal: Soho.Locale.numbers().decimal,
                negative: Soho.Locale.numbers().minusSign,
                thousands: Soho.Locale.numbers().group
              }
            }
          };

          if (type === AppConstants.PERCENT) {
            column.serialize = this.LMPercentSerializer;
          } else if (type === AppConstants.BCD) {
            column.serialize = this.LMBcdSerializer;
          }
          break;

        case AppConstants.YYYYMM:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMPeriodSerializer;
          break;

        case AppConstants.TIME:
          column.editor = Soho.Editors.Time;
          column.serialize = this.LMTimeSerializer;
          break;

        case AppConstants.TIMESTAMP:
          column.editor = Soho.Editors.Date;

          if (column.displayField.displayDateOnly) {
            column.serialize = this.LMDateSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.short;

          } else if (column.displayField.displayTimeOnly) {
            column.serialize = this.LMTimestampSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.timestamp;
            column.editorOptions = {
              showDate: false,
              showTime: true,
              dateFormat: Soho.Locale.calendar().dateFormat.time
            };

          } else {
            column.serialize = this.LMTimestampSerializer;
            column.dateShowFormat = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;
            column.editorOptions = {
              secondInterval: 1,
              minuteInterval: 1,
              showTime: true,
              showMonthYearPicker: true,
              dateFormat: Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp
            };
          }
          break;

        case AppConstants.MMDD:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMAnniversarySerializer;
          break;

        case AppConstants.YYYY:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMYearSerializer;
          column.editorOptions = { showMonthYearPicker: true, hideDays: true, dateFormat: 'yyyy' };
          break;

        case AppConstants.YYYYMMDD:
          column.editor = Soho.Editors.Date;
          column.serialize = this.LMDateSerializer;
          break;

        case AppConstants.BOOLEAN:
          column.editor = Soho.Editors.Dropdown;
          column.serialize = this.LMBooleanSerializer;
          column.options =
            [
              {
                label: Soho.Locale.translate('Yes'),
                value: true
              },
              {
                label: Soho.Locale.translate('No'),
                value: false
              }
            ];
          break;

        case AppConstants.TEXT:
          column.editor = Soho.Editors.Input;
          break;

        case AppConstants.TEXTAREA:
          column.editor = Soho.Editors.Textarea;
          break;

        case AppConstants.CHECKCONTROL:
          // if (column.displayField.validity) {
          //   column.isEditable = this.LMIsCheckControlEditable;
          // }
          column.editor = (cellDisplayField.displayAsStar) ? Soho.Editors.Favorite : Soho.Editors.Checkbox;
          break;

        case 'CURRENCY':
        case 'ALPHAUPPER':
        case 'ALPHALOWER':
        case 'ALPHARIGHT':
        case 'BINARY':
          column.editor = Soho.Editors.Input;
          column.uppercase = (type === 'ALPHAUPPER');
          if (cellDisplayField.size) {
            column.maxLength = cellDisplayField.size;
          }
          break;

        default:
          column.editor = Soho.Editors.Input;
          if (cellDisplayField.size) {
            column.maxLength = cellDisplayField.size;
          }
          break;
      }

      if (cellDisplayField.selectable) {
        // todo spec-url-refactor - matrix doens't seem to imeplment the selectListUrl. If we need to impl this
        //    in the future if shnould look like how lm-list-grid-builder.service.ts does it.
        // const selectListUrl = this.lmUrlUtilService.buildSelectListUrl(listUrlInfo, displayField.selectListInfo);
        // if (!this.lmContextService.useNewSpecUrls()) {
        //   if (this.lmContextService.compareSpecUrls()) {
        //     this.lmUrlInfoBuilderService.compareSpecUrls(displayField.bindId + '.selectListUrl', (displayField as any).selectListUrl, selectListUrl);
        //   }
        // }
        // TODO DPB need to reexamine lookup select in a matrix
        //   column.editor = Soho.Editors.Lookup;
        //   column.editorOptions = {
        //     editable: true,
        //     click: this.LMLookupClickFunction,
        //     clickArguments: {
        //       column: {
        //         displayField: cellDisplayField
        //       }
        //     }
        //   };
      }
    }

  }

  private buildMatrixData(rowDataViewsSet: LMDataViewSet): LMDataView[] {
    this.lmFormMatrixService.copyStateValuesToCellView(rowDataViewsSet);
    return rowDataViewsSet.data;
  }

  private getDirtyCells(): Array<any> {
    if (this.sohoDataGridComponent) {
      return this.sohoDataGridComponent.dirtyCells();
    } else {
      return [];
    }
  }

  private handleError(error: any) {
    this.lmErrorUtilService.handleUnexpectedError(error, this.matrixDialog);
  }

  LMMatrixRowFormatter = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): string => lmMatrixRowFormatter(
    this.lmFormatUtilService,
    this.lmConditionUtilService,
    this.matrixFormSpec,
    this.showDetailFields,
    row, cell, value, columnDef, dataView, dataGrid);

  // landmark editors
  // LMIsCheckControlEditable: LMListGridColumnIsEditable = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: any, dataGrid: SohoDataGridStatic): boolean => {
  //   return isCheckControlEditable(this.lmEditorService, row, cell, value, columnDef, dataView, dataGrid);
  // };
  //
  // LMIsEditable = (row: number, cell: number, value: any, columnDef: ListGridColumn, dataView: LMDataView, dataGrid: SohoDataGridStatic): boolean => {
  //   return isEditable(this.lmEditorService, this.lmActionService, columnDef, dataView);
  // };
  //
  // LMStateFieldSource = (response: any, searchTerm: any, sourceArguments: any) => {
  //   return lmStateFieldSource(this.lmEditorService, this.lmFieldUtilService, this.lmFormatUtilService, response, searchTerm, sourceArguments);
  // };

  LMLookupClickFunction = (event: any, lookup: SohoLookupStatic, clickArguments: any) =>
    // todo spec-url-refactor need to set selectListUrl in clickArguments? cannot use selectListUrl from column spec any more.
    lmLookupClickFunction(this.lmFormatUtilService, event, lookup, clickArguments)
  ;
  // landmark serializers
  LMBcdSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string => {
    const decSize: number = columnDef.cellFieldSpec.decSize;
    const precision: number = dataView.fields[ columnDef.field ] && dataView.fields[ columnDef.field ].precision !== undefined
      ? dataView.fields[ columnDef.field ].precision
      : columnDef.cellFieldSpec.decSize;

    return this.lmFormatUtilService.bcdToData(value, { decSize, precision });
  };

  LMBooleanSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): boolean =>
    lmBooleanSerializer(this.lmFormatterService, this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView);

  LMPercentSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmPercentSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView);

  LMYearSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYY);

  LMPeriodSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYYMM);

  LMAnniversarySerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.MMDD);

  LMDateSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.YYYYMMDD);

  LMTimestampSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.TIMESTAMP);

  LMTimeSerializer = (value: string, oldValue: string, columnDef: ListGridColumn, row: number, cell: number, dataView: any): string =>
    lmDateTimeSerializer(this.lmFormatUtilService, value, oldValue, columnDef, row, cell, dataView, AppConstants.TIME);
}
